import { Token, TokenizerResult, TokenType } from './types';

export class FilterTokenizer {
  private static readonly OPERATORS = [
    '>=',
    '<=',
    '!=',
    '=',
    '>',
    '<',
    'BOOL',
    'INSIDE_AREA',
    'OUTSIDE_AREA',
    'INSIDE',
    'OUTSIDE',
  ];
  private static readonly LOGICAL_OPS = ['AND', 'OR'];

  static tokenize(input: string, cursorPosition?: number): TokenizerResult {
    const tokens: Token[] = [];
    let position = 0;

    const pattern =
      /("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')|(INSIDE_AREA|OUTSIDE_AREA|INSIDE|OUTSIDE|>=|<=|!=|=|>|<)|(\b(?:AND|OR)\b)|(\w+)|(\(|\))|(\s+)/gi;

    let match;

    while ((match = pattern.exec(input)) !== null) {
      const [
        fullMatch,
        quotedValue,
        operator,
        logical,
        word,
        parenthesis,
        whitespace,
      ] = match;

      if (whitespace) {
        position += fullMatch.length;
        continue; // Skip whitespace
      }

      let token: Token;
      const start = position;
      const end = position + fullMatch.length;

      if (quotedValue) {
        // Remove quotes for value but keep raw
        token = {
          type: 'value',
          value: quotedValue.slice(1, -1),
          raw: quotedValue,
          start,
          end,
        };
      } else if (operator && this.OPERATORS.includes(operator)) {
        token = {
          type: 'operator',
          value: operator,
          raw: operator,
          start,
          end,
        };
      } else if (logical && this.LOGICAL_OPS.includes(logical.toUpperCase())) {
        token = {
          type: 'logical',
          value: logical.toUpperCase(),
          raw: fullMatch,
          start,
          end,
        };
      } else if (parenthesis) {
        token = {
          type: 'parenthesis',
          value: parenthesis,
          raw: parenthesis,
          start,
          end,
        };
      } else if (word) {
        // Determine if it's a field or value based on context
        const prevToken = tokens[tokens.length - 1];
        const isValue = prevToken?.type === 'operator';

        token = {
          type: isValue ? 'value' : 'field',
          value: word,
          raw: word,
          start,
          end,
        };
      } else {
        position += fullMatch.length;
        continue;
      }

      tokens.push(token);
      position += fullMatch.length;
    }

    // Determine cursor context if position provided
    let cursorContext = null;
    let currentToken = null;

    if (cursorPosition !== undefined) {
      // Find token at cursor or determine position between tokens
      for (let i = 0; i < tokens.length; i++) {
        if (
          cursorPosition >= tokens[i].start &&
          cursorPosition <= tokens[i].end
        ) {
          currentToken = tokens[i];
          cursorContext = {
            insideToken: true,
            tokenIndex: i,
            positionInToken: cursorPosition - tokens[i].start,
          };
          break;
        }
      }

      if (!currentToken) {
        // Cursor is between tokens or at the end
        const beforeTokens = tokens.filter((t) => t.end <= cursorPosition);
        cursorContext = {
          insideToken: false,
          tokenIndex: beforeTokens.length,
          positionInToken: 0,
        };
      }
    }

    return {
      tokens,
      currentToken,
      expectedNext: this.getExpectedTokenType(tokens, cursorPosition, input),
      cursorContext,
    };
  }

  private static getExpectedTokenType(
    tokens: Token[],
    cursorPosition?: number,
    input?: string
  ): TokenType {
    // If we have a cursor position and input
    if (cursorPosition !== undefined && input) {
      const textBeforeCursor = input.substring(0, cursorPosition);
      const endsWithSpace = textBeforeCursor.endsWith(' ');

      // Check if we're inside an incomplete quoted value
      const quoteContext = this.getQuoteContext(textBeforeCursor);

      // If we're inside an incomplete quote, continue suggesting values
      if (quoteContext.isInsideQuote) {
        return TokenType.Value;
      }

      // Check if we're in the middle of typing an unquoted token
      const isTypingToken =
        !endsWithSpace &&
        textBeforeCursor.length > 0 &&
        !textBeforeCursor.endsWith('"') &&
        !textBeforeCursor.endsWith("'");

      // If we're typing a token and it's not complete, determine what we're typing
      if (isTypingToken) {
        const lastMeaningfulToken = tokens
          .filter((t) => t.type !== 'parenthesis')
          .pop();

        if (!lastMeaningfulToken) {
          // No previous token, must be typing a field
          return TokenType.Field;
        }

        switch (lastMeaningfulToken.type) {
          case 'field':
            // After a field, we expect an operator
            return TokenType.ArithmeticOperator;
          case 'operator':
            // After an operator, we expect a value
            return TokenType.Value;
          case 'value':
            // After a value, only suggest logical if we have space or completed token
            return TokenType.Value; // Keep suggesting value until space is typed
          case 'logical':
            // After logical operator, expect field
            return TokenType.Field;
          default:
            return TokenType.Field;
        }
      }

      // If we end with space, we're starting a new token
      if (endsWithSpace) {
        const lastMeaningfulToken = tokens
          .filter((t) => t.type !== 'parenthesis')
          .pop();

        if (!lastMeaningfulToken) return TokenType.Field;

        switch (lastMeaningfulToken.type) {
          case 'field':
            return TokenType.ArithmeticOperator;
          case 'operator':
            return TokenType.Value;
          case 'value':
            return TokenType.LogicalOperator;
          case 'logical':
            return TokenType.Field;
          default:
            return TokenType.Field;
        }
      }

      // If we're at the end of a completed quoted value, suggest logical
      if (textBeforeCursor.endsWith('"') || textBeforeCursor.endsWith("'")) {
        const lastToken = tokens[tokens.length - 1];
        if (lastToken && lastToken.type === 'value') {
          return TokenType.LogicalOperator;
        }
      }
    }

    // Default behavior for when we don't have cursor context
    if (tokens.length === 0) return TokenType.Field;

    const lastMeaningfulToken = tokens
      .filter((t) => t.type !== 'parenthesis')
      .pop();

    if (!lastMeaningfulToken) return TokenType.Field;

    switch (lastMeaningfulToken.type) {
      case 'field':
        return TokenType.ArithmeticOperator;
      case 'operator':
        return TokenType.Value;
      case 'value':
        return TokenType.LogicalOperator;
      case 'logical':
        return TokenType.Field;
      default:
        return TokenType.Field;
    }
  }

  // NEW: Consolidated quote context method - single source of truth
  static getQuoteContext(text: string): {
    isInsideQuote: boolean;
    quoteChar: '"' | "'" | null;
    quoteStartIndex: number;
    quoteContent: string;
  } {
    let inSingleQuote = false;
    let inDoubleQuote = false;
    let lastQuoteStart = -1;
    let quoteChar: '"' | "'" | null = null;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const prevChar = i > 0 ? text[i - 1] : '';

      // Skip escaped quotes
      if (prevChar === '\\') {
        continue;
      }

      if (char === '"' && !inSingleQuote) {
        if (!inDoubleQuote) {
          inDoubleQuote = true;
          lastQuoteStart = i;
          quoteChar = '"';
        } else {
          inDoubleQuote = false;
          lastQuoteStart = -1;
          quoteChar = null;
        }
      } else if (char === "'" && !inDoubleQuote) {
        if (!inSingleQuote) {
          inSingleQuote = true;
          lastQuoteStart = i;
          quoteChar = "'";
        } else {
          inSingleQuote = false;
          lastQuoteStart = -1;
          quoteChar = null;
        }
      }
    }

    const isInsideQuote = inSingleQuote || inDoubleQuote;
    const quoteContent =
      isInsideQuote && lastQuoteStart >= 0
        ? text.substring(lastQuoteStart + 1)
        : '';

    return {
      isInsideQuote,
      quoteChar,
      quoteStartIndex: lastQuoteStart,
      quoteContent,
    };
  }

  static extractIncompleteQuoteContent(
    input: string,
    cursorPosition: number
  ): string | null {
    const textBeforeCursor = input.substring(0, cursorPosition);
    const quoteContext = this.getQuoteContext(textBeforeCursor);

    return quoteContext.isInsideQuote ? quoteContext.quoteContent : null;
  }
}
